import {
  CalendarTimeRangeConfig,
  FixedTimeRangeConfig,
} from '@highimpact-frontend/api-types';
import dayjs from 'dayjs';
import { buildFixedRangeName } from './FixedTimeRange';
import { timeDivisibility } from './periodDivisibility';

type ParsedDefault = {
  start: Date;
  end: Date;
  unit:
    | 'millisecond'
    | 'second'
    | 'minute'
    | 'hour'
    | 'day'
    | 'week'
    | 'month'
    | 'year';
};

export function parseDefaultIntervals(
  intervals?: (CalendarTimeRangeConfig | FixedTimeRangeConfig)[]
): ParsedDefault | undefined {
  if (!intervals || intervals.length === 0) return undefined;

  if (
    intervals.every(
      (i): i is FixedTimeRangeConfig => i.type === 'fixed_time_range'
    )
  ) {
    const minDate = new Date(
      Math.min(...intervals.map((i) => new Date(i.startdate).getTime()))
    );
    const maxDate = new Date(
      Math.max(...intervals.map((i) => new Date(i.enddate).getTime()))
    );
    const divisibility = timeDivisibility(minDate, maxDate);
    const unit = divisibility.isExactYears
      ? 'year'
      : divisibility.isExactMonths
      ? 'month'
      : divisibility.isExactWeeks
      ? 'week'
      : divisibility.isExactDays
      ? 'day'
      : divisibility.isExactHours
      ? 'hour'
      : divisibility.isExactMinutes
      ? 'minute'
      : divisibility.isExactSeconds
      ? 'second'
      : divisibility.isExactMilliseconds
      ? 'millisecond'
      : undefined;
    return unit
      ? {
          start: minDate,
          end: maxDate,
          unit,
        }
      : undefined;
  }

  if (
    intervals.every(
      (i): i is CalendarTimeRangeConfig => i.type === 'calendar_time_range'
    )
  ) {
    // sanity: all items must have the same unit, relative_to, quantity, direction
    const first = intervals[0];

    // unit must be 'months' or 'years'
    if (!(first.unit === 'months' || first.unit === 'years')) return undefined;

    const sameUnit = intervals.every((it) => it.unit === first.unit);
    if (!sameUnit) return undefined;

    const sameRelativeTo = intervals.every(
      (it) => it.relative_to === first.relative_to
    );
    if (!sameRelativeTo) return undefined;

    // relative_to must be a date string and not the literal text 'now'
    if (first.relative_to === 'now') return undefined;
    const endCandidate = dayjs(first.relative_to);
    if (!endCandidate.isValid()) return undefined;

    const sameQuantity = intervals.every(
      (it) => it.quantity === first.quantity
    );
    if (!sameQuantity) return undefined;

    const sameDirection = intervals.every(
      (it) => it.direction === first.direction
    );
    if (!sameDirection) return undefined;

    // period_shift must be non-null for all items and strictly decreasing from each item to the next
    const shifts = intervals.map((it) => it.period_shift);
    if (shifts.some((s) => s === null || s === undefined)) return undefined;
    for (let i = 1; i < shifts.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (!(shifts[i - 1]! > shifts[i]!)) return undefined; // strictly decreasing
    }

    // enddate is the relative_to value of the last item
    const last = intervals[intervals.length - 1];
    if (last.relative_to === 'now') return undefined;
    const endDate = dayjs(last.relative_to);
    if (!endDate.isValid()) return undefined;

    // compute start date: remove amount * period_shift units of the first item from the enddate
    const amount = first.quantity * (first.period_shift ?? 0);
    const unit = first.unit === 'months' ? 'month' : 'year';
    const startDate = endDate.subtract(amount, unit);

    return {
      start: startDate.toDate(),
      end: endDate.toDate(),
      unit: unit === 'month' ? 'month' : 'year',
    };
  }

  // unhandled mixed intervals
  return undefined;
}

/**
 * Build a list of CalendarTimeRange or FixedTimeRange objects.
 *
 * @param start        Range start date
 * @param end          Range end date
 * @param unit         'month' or 'year'
 * @param formatter    Intl.DateTimeFormat configured for month or year labels
 * @param noDivision   When true return a single FixedTimeRangeConfig covering the whole range
 */
export function buildCalendarTimeRanges(
  start: Date,
  end: Date,
  unit:
    | 'millisecond'
    | 'second'
    | 'minute'
    | 'hour'
    | 'day'
    | 'week'
    | 'month'
    | 'year',
  formatter: Intl.DateTimeFormat,
  noDivision = false
): (CalendarTimeRangeConfig | FixedTimeRangeConfig)[] {
  const startDate = dayjs(start).startOf(unit);
  const endDate = dayjs(end).startOf(unit);

  if (noDivision) {
    const name = buildFixedRangeName(start, end);
    const fixed: FixedTimeRangeConfig = {
      type: 'fixed_time_range',
      name,
      campaign_ids: null,
      startdate: dayjs(start).toISOString(),
      enddate: dayjs(end).toISOString(),
    };
    return [fixed];
  }

  const ranges: CalendarTimeRangeConfig[] = [];
  let current = startDate;

  while (current.isBefore(endDate) || current.isSame(endDate)) {
    const dateObj = current.toDate();
    const shift = endDate.diff(current, unit);

    ranges.push({
      type: 'calendar_time_range',
      name: formatter.format(dateObj),
      unit:
        unit === 'millisecond'
          ? 'millisecondes'
          : ((unit + 's') as `${typeof unit}s`),
      quantity: 1,
      period_shift: shift,
      direction: 'before',
      relative_to: end.toISOString(),
    });

    current = current.add(1, unit);
  }

  return ranges;
}
